import { UniqueIdentifier } from "@dnd-kit/core";
import { DropId, PlayState, PlayableVariant, TabName, TAB_NAME_ZONE_NAME_MAP } from "./types";
import { Identifiable } from "../DragAndDrop/types";
import { useEffect, useState } from "react";
import { isTruthy } from "../../functions/Common";

export const getContainerIdByChildId = (id: DropId, items: PlayState): UniqueIdentifier | undefined => {
    if (id in items) {
        return id;
    }

    if (id in TAB_NAME_ZONE_NAME_MAP) {
        return TAB_NAME_ZONE_NAME_MAP[id as TabName];
    }

    return Object.keys(items).find((key) => includes(items[key as keyof PlayState], id)) as UniqueIdentifier | undefined;
};

export function includes(values: Identifiable[], id: UniqueIdentifier) {
    return !!values.find(x => {
        return x !== null && x !== undefined && x.id === id;
    });
}

export function useMountStatus() {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setIsMounted(true), 500);

        return () => clearTimeout(timeout);
    }, []);

    return isMounted;
}

export function moveToTopAndSave(
    card: PlayableVariant,
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    state: PlayState,
    setState: React.Dispatch<React.SetStateAction<PlayState>>,
    setHasMulliganed: React.Dispatch<React.SetStateAction<boolean>>
) {
    setHasMulliganed(true);
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
    moveToTop(card, sourceId, destinationId, stateCopy);
    setState(stateCopy);
}

export function moveToTop(
    card: PlayableVariant,
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    stateCopy: PlayState
) {
    const result = validateDestination(sourceId, destinationId, stateCopy);
    if (!result) {
        return;
    }

    if (card.value.card.type === "Backup") {
        card.value.isDull = !sourceId.includes("field") && destinationId.includes("field");
    }

    card.value.isFrozen = false;

    if (sourceId == destinationId) {
        return;
    }

    // remove from source array
    stateCopy[sourceId as keyof PlayState] = result.source.filter(x => isTruthy(x) && x.id !== card.id);
    result.destination.push(card);
}

export function moveMany(
    cards: PlayableVariant[],
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    state: PlayState,
    setState: React.Dispatch<React.SetStateAction<PlayState>>,
    setHasMulliganed: React.Dispatch<React.SetStateAction<boolean>>,
    toStart?: boolean
) {
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
    setHasMulliganed(true);

    const result = validateDestination(sourceId, destinationId, stateCopy);
    if (!result) {
        return;
    }

    if (sourceId == destinationId) {
        return;
    }

    // remove from source array
    stateCopy[sourceId as keyof PlayState] = result.source.filter(x => isTruthy(x) && cards.findIndex((card) => card.id === x.id) === -1);

    if (toStart) {
        stateCopy[destinationId as keyof PlayState] = [...cards, ...result.destination];
    } else {
        stateCopy[destinationId as keyof PlayState].push(...cards);
    }

    setState(stateCopy);
}

export function moveToBottomAndSave(
    card: PlayableVariant,
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    state: PlayState,
    setState: React.Dispatch<React.SetStateAction<PlayState>>,
    setHasMulliganed: React.Dispatch<React.SetStateAction<boolean>>
) {
    setHasMulliganed(true);
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
    moveToBottom(card, sourceId, destinationId, stateCopy);
    setState(stateCopy);
}

export function moveToBottom(
    card: PlayableVariant,
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    stateCopy: PlayState
) {
    const result = validateDestination(sourceId, destinationId, stateCopy);
    if (result === null) {
        return;
    }

    card.value.isDull = false;
    card.value.isFrozen = false;
    card.value.counter = 0;

    // remove from source array
    stateCopy[sourceId as keyof PlayState] = result.source.filter(x => isTruthy(x) && x.id !== card.id);
    stateCopy[destinationId] = [card, ...result.destination];
}

export function setIsDullAndSave(card: PlayableVariant, isDull: boolean, state: PlayState, setState: React.Dispatch<React.SetStateAction<PlayState>>) {
    setIsDull(card, isDull);
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
    setState(stateCopy);
}

export function setIsDull(card: PlayableVariant, isDull: boolean) {
    if (card.value.card.type === "Summon") {
        card.value.isDull = false;
        return;
    }

    card.value.isDull = isDull;
}

export function setIsFrozenAndSave(card: PlayableVariant, isFrozen: boolean, state: PlayState, setState: React.Dispatch<React.SetStateAction<PlayState>>) {
    setIsFrozen(card, isFrozen);
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
    setState(stateCopy);
}

export function setIsFrozen(card: PlayableVariant, isFrozen: boolean) {
    if (card.value.card.type === "Summon") {
        card.value.isFrozen = false;
        return;
    }

    card.value.isFrozen = isFrozen;
}

export function activateAll(state: PlayState, setState: React.Dispatch<React.SetStateAction<PlayState>>) {
    const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;

    const fields = Object.entries(stateCopy).filter(([key, value]) => key.includes("field"));
    fields.forEach(([key, values]) => {
        values.filter(x => isTruthy(x) && !x.value.isFrozen).forEach(x => x.value.isDull = false);
        values.filter(x => isTruthy(x) && x.value.isFrozen).forEach(x => x.value.isFrozen = false);
    });

    setState(stateCopy);
}

export function validateDestination(
    sourceId: keyof PlayState,
    destinationId: keyof PlayState,
    stateCopy: PlayState
) {
    if (sourceId === destinationId) {
        return null;
    }

    const source = stateCopy[sourceId];
    if (!source) {
        return null;
    }

    const destination = stateCopy[destinationId];
    if (!destination) {
        return null;
    }

    return {
        source,
        sourceId,
        destination
    }
}

export function shuffleArrayAndSave(
    state: PlayState,
    zone: keyof PlayState,
    setState: React.Dispatch<React.SetStateAction<PlayState>>
) {
    const stateCopy: PlayState = JSON.parse(JSON.stringify(state));

    for (let i = stateCopy[zone].length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = stateCopy[zone][i];
        stateCopy[zone][i] = stateCopy[zone][j];
        stateCopy[zone][j] = temp;
    }

    setState(stateCopy);
}